import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import api from "../../services/api";
import "./Profile.css";

const Profile = () => {
  // Try to get initial profile data from localStorage
  const getInitialProfile = () => {
    try {
      const storedProfile = localStorage.getItem("profile");
      if (storedProfile) {
        return JSON.parse(storedProfile);
      }

      // Basic fallback if no full profile is stored
      return {
        name: localStorage.getItem("name") || "",
        email: localStorage.getItem("email") || "",
        bio: localStorage.getItem("bio") || "",
        avatar_url: localStorage.getItem("avatar_url") || "/default-avatar.png",
      };
    } catch (error) {
      console.error("Error parsing stored profile:", error);
      return null;
    }
  };

  // Initialize state with data from localStorage if available
  const [profile, setProfile] = useState(getInitialProfile());
  const [preferences, setPreferences] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [progress, setProgress] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [avatar, setAvatar] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Check if we navigated back from profile edit
    if (location.state?.profileUpdated) {
      console.log("🔄 Profile updated, refreshing profile data");
      fetchUserData();
      // Clear the state to prevent repeated refreshes
      window.history.replaceState({}, document.title);
    }

    fetchUserData();
  }, [location.state?.profileUpdated]);

  const fetchUserData = async () => {
    try {
      const [profileRes, prefsRes, achievementsRes, progressRes] =
        await Promise.all([
          api.get("/profile/me"),
          api.get("/profile/me/preferences"),
          api.get("/profile/me/achievements"),
          api.get("/profile/me/progress"),
        ]);

      // Update state with response data
      setProfile(profileRes.data);
      setPreferences(prefsRes.data);
      setAchievements(achievementsRes.data);
      setProgress(progressRes.data);

      // Also update localStorage for consistent data access across components
      localStorage.setItem("name", profileRes.data.name || "");
      localStorage.setItem("email", profileRes.data.email || "");
      if (profileRes.data.bio) localStorage.setItem("bio", profileRes.data.bio);
      if (profileRes.data.avatar_url)
        localStorage.setItem("avatar_url", profileRes.data.avatar_url);

      // Store the entire objects for easier access
      localStorage.setItem("profile", JSON.stringify(profileRes.data));
      localStorage.setItem("preferences", JSON.stringify(prefsRes.data));
    } catch (error) {
      setError("Error loading profile data");
      console.error("Error:", error);

      // Fall back to localStorage data if available
      try {
        const storedProfile = localStorage.getItem("profile");
        const storedPreferences = localStorage.getItem("preferences");

        if (storedProfile) {
          setProfile(JSON.parse(storedProfile));
        } else {
          // Basic fallback
          setProfile({
            name: localStorage.getItem("name") || "",
            email: localStorage.getItem("email") || "",
            bio: localStorage.getItem("bio") || "",
            avatar_url:
              localStorage.getItem("avatar_url") || "/default-avatar.png",
          });
        }

        if (storedPreferences) {
          setPreferences(JSON.parse(storedPreferences));
        }
      } catch (localStorageError) {
        console.error("Error loading from localStorage:", localStorageError);
      }
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put("/profile/me", profile);
      setProfile(response.data);

      // Also update localStorage with the updated profile data
      if (response.data) {
        localStorage.setItem("name", response.data.name || "");
        localStorage.setItem("email", response.data.email || "");
        if (response.data.bio) localStorage.setItem("bio", response.data.bio);
        if (response.data.avatar_url)
          localStorage.setItem("avatar_url", response.data.avatar_url);

        // Store the entire profile object for easier access
        localStorage.setItem("profile", JSON.stringify(response.data));
      }

      setMessage("Profile updated successfully");
      setIsEditing(false);
    } catch (error) {
      setError("Error updating profile");
      console.error("Error:", error);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("avatar", file);
      try {
        const response = await api.post("/profile/me/avatar", formData);
        const updatedProfile = {
          ...profile,
          avatar_url: response.data.avatar_url,
        };
        setProfile(updatedProfile);

        // Update localStorage with new avatar URL
        localStorage.setItem("avatar_url", response.data.avatar_url);
        // Update the entire profile object in localStorage
        localStorage.setItem("profile", JSON.stringify(updatedProfile));

        setMessage("Avatar updated successfully");
      } catch (error) {
        setError("Error updating avatar");
        console.error("Error:", error);
      }
    }
  };

  const handlePreferencesUpdate = async (newPreferences) => {
    try {
      const response = await api.put("/profile/me/preferences", newPreferences);
      setPreferences(response.data);

      // Save preferences to localStorage
      if (response.data) {
        // Store individual preferences that might be needed elsewhere
        if (response.data.learningStyle) {
          localStorage.setItem("learning_style", response.data.learningStyle);
        }
        if (response.data.difficultyLevel) {
          localStorage.setItem(
            "difficulty_level",
            response.data.difficultyLevel
          );
        }

        // Store the entire preferences object
        localStorage.setItem("preferences", JSON.stringify(response.data));
      }

      setMessage("Preferences updated successfully");
    } catch (error) {
      setError("Error updating preferences");
      console.error("Error:", error);
    }
  };

  if (!profile) {
    return <div>Loading...</div>;
  }

  return (
    <Container className="profile-container">
      {message && <Alert variant="success">{message}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        <Col md={4}>
          <Card className="profile-card">
            <Card.Body>
              <div className="avatar-container">
                <img
                  src={profile.avatar_url || "/default-avatar.png"}
                  alt="Profile"
                  className="profile-avatar"
                />
                <div className="avatar-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    id="avatar-input"
                    hidden
                  />
                  <label
                    htmlFor="avatar-input"
                    className="btn btn-outline-primary"
                  >
                    Update Photo
                  </label>
                </div>
              </div>

              {isEditing ? (
                <Form onSubmit={handleProfileUpdate}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={profile.name}
                      onChange={(e) =>
                        setProfile({ ...profile, name: e.target.value })
                      }
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Bio</Form.Label>
                    <Form.Control
                      as="textarea"
                      value={profile.bio || ""}
                      onChange={(e) =>
                        setProfile({ ...profile, bio: e.target.value })
                      }
                    />
                  </Form.Group>
                  <Button type="submit" className="mt-3">
                    Save Changes
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="mt-3 ml-2"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </Button>
                </Form>
              ) : (
                <>
                  <h3>{profile.name}</h3>
                  <p>{profile.bio || "No bio yet"}</p>
                  <Button
                    variant="outline-primary"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Profile
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>Learning Progress</Card.Header>
            <Card.Body>
              {Object.entries(progress).map(([subject, data]) => (
                <div key={subject} className="progress-item">
                  <h5>{subject}</h5>
                  <div className="progress">
                    <div
                      className="progress-bar"
                      style={{ width: `${data.percentage}%` }}
                    >
                      {data.percentage}%
                    </div>
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>Achievements</Card.Header>
            <Card.Body>
              <div className="achievements-grid">
                {achievements.map((achievement) => (
                  <div key={achievement.id} className="achievement-item">
                    <img src={achievement.icon} alt={achievement.name} />
                    <h6>{achievement.name}</h6>
                    <p>{achievement.description}</p>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>Learning Preferences</Card.Header>
            <Card.Body>
              <Form>
                <Form.Group>
                  <Form.Label>Preferred Learning Style</Form.Label>
                  <Form.Control
                    as="select"
                    value={preferences?.learningStyle || ""}
                    onChange={(e) =>
                      handlePreferencesUpdate({
                        ...preferences,
                        learningStyle: e.target.value,
                      })
                    }
                  >
                    <option value="visual">Visual</option>
                    <option value="auditory">Auditory</option>
                    <option value="reading">Reading/Writing</option>
                    <option value="kinesthetic">Kinesthetic</option>
                  </Form.Control>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Difficulty Level</Form.Label>
                  <Form.Control
                    as="select"
                    value={preferences?.difficultyLevel || ""}
                    onChange={(e) =>
                      handlePreferencesUpdate({
                        ...preferences,
                        difficultyLevel: e.target.value,
                      })
                    }
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </Form.Control>
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;
